import React, { useState, useEffect } from 'react';
import Form from '../Form/Form';
import InputDate from '../Form/Fields/InputDate';
import InputMedicalService from '../Form/Fields/InputMedicalService';
import InputPatient from '../Form/Fields/InputPatient';
import Textarea from '../Form/Fields/Textarea';
import InputText from '../Form/Fields/InputText';
import Button from '../Form/Button';
import Modal from '../Modal/Modal';
import './AppointmentPopover.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCheck, faStethoscope, faListCheck, faBolt } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';

const TYPES = [
  { key: 'SERVICE', label: 'Service',    icon: faStethoscope, color: '#2563eb' },
  { key: 'TASK',    label: 'Tâche',      icon: faListCheck,   color: '#d97706' },
  { key: 'EVENT',   label: 'Événement',  icon: faBolt,        color: '#7c3aed' },
];

const AppointmentPopover = ({
  show,
  onHide,
  onSave,
  defaultValues,
}) => {
  const { t } = useTranslation();
  const { hasRight } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [apptType, setApptType] = useState('SERVICE');
  const isEdit = !!defaultValues?.id;
  const canSave = isEdit ? hasRight('CALENDAR_EDIT') : hasRight('CALENDAR_CREATE');

  useEffect(() => {
    setApptType(defaultValues?.appointmentType || 'SERVICE');
  }, [defaultValues]);

  const onSubmit = (data) => {
    setIsLoading(true);
    const method = isEdit ? 'put' : 'post';
    const url = isEdit
      ? `${API_BASE_URL}/api/v1/updateAppointment/${defaultValues.id}`
      : `${API_BASE_URL}/api/v1/addAppointment`;

    axios[method](url, { ...data, appointmentType: apptType }, { withCredentials: true })
      .then((response) => {
        toast.success(t('Appointment saved successfully!'));
        onHide?.();
        onSave?.(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error saving appointment:', error);
        toast.error(t('An error occurred while saving the appointment.'));
        setIsLoading(false);
      });
  };

  return (
    <Modal
      id="appointmentModal"
      show={show}
      onClose={onHide}
      title={isEdit ? t('Modifier le rendez-vous') : t('Nouveau rendez-vous')}
      icon={faCalendarDays}
      iconBg="#f3f4f6"
      iconColor="#374151"
      footer={
        canSave ? (
          <Button
            type="submit"
            form="appointmentPopoverForm"
            className="appt-btn appt-btn--submit"
            isLoading={isLoading}
            loadingMessage={t('Enregistrement...')}
          >
            <FontAwesomeIcon icon={faCheck} />
            {t('Enregistrer')}
          </Button>
        ) : null
      }
    >
      <Form id="appointmentPopoverForm" onSubmit={onSubmit} defaultValues={defaultValues}>

        {/* Type selector */}
        <div className="appt-field">
          <label className="appt-field__label">Type</label>
          <div className="appt-type-selector">
            {TYPES.map(({ key, label, icon, color }) => {
              const active = apptType === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`appt-type-btn${active ? ' appt-type-btn--active' : ''}`}
                  style={active ? { background: color + '14', borderColor: color, color } : {}}
                  onClick={() => setApptType(key)}
                >
                  <FontAwesomeIcon icon={icon} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* SERVICE fields */}
        {apptType === 'SERVICE' && (
          <>
            <div className="appt-field">
              <label className="appt-field__label">{t('Medical Service')}</label>
              <InputMedicalService
                name="medicalServiceId"
                validationRules={{ required: t('Medical Service is required') }}
              />
            </div>
            <div className="appt-field">
              <label className="appt-field__label">{t('Patient')}</label>
              <InputPatient
                name="patientId"
                validationRules={{ required: t('Patient is required') }}
              />
            </div>
          </>
        )}

        {/* TASK / EVENT title */}
        {apptType !== 'SERVICE' && (
          <div className="appt-field">
            <label className="appt-field__label">{t('Titre')}</label>
            <InputText
              name="title"
              placeholder={apptType === 'TASK' ? 'Ex : Réapprovisionnement...' : 'Ex : Réunion équipe...'}
              validationRules={{ required: t('Le titre est requis') }}
            />
          </div>
        )}

        {/* Dates */}
        <div className="appt-field">
          <label className="appt-field__label">{t('Date & heure')}</label>
          <div className="appt-date-stack">
            <div className="appt-date-col">
              <span className="appt-date-sublabel">{t('Du')}</span>
              <InputDate
                name="fromDate"
                validationRules={{ required: t('Start date is required') }}
                mode='datetime'
              />
            </div>
            <div className="appt-date-col">
              <span className="appt-date-sublabel">{t('Au')}</span>
              <InputDate
                name="toDate"
                validationRules={{ required: t('End date is required') }}
                mode='datetime'
              />
            </div>
          </div>
        </div>

        {/* SERVICE optional title */}
        {apptType === 'SERVICE' && (
          <div className="appt-field">
            <label className="appt-field__label">
              {t('Titre')}
              <span className="appt-field__optional">{t('optionnel')}</span>
            </label>
            <InputText
              name="title"
              placeholder={t('Ex : Douleurs abdominales...')}
            />
          </div>
        )}

        <div className="appt-field">
          <label className="appt-field__label">
            {t('Description')}
            <span className="appt-field__optional">{t('optionnel')}</span>
          </label>
          <Textarea
            name="description"
            placeholder={t('Ajoutez des notes...')}
          />
        </div>

      </Form>
    </Modal>
  );
};

export default AppointmentPopover;
